// Feature detail. For editors, the same layout viewers see, but each
// value is an inline input — no separate "Edit" mode. Save button in
// the actions row commits everything at once. For viewers/sales,
// static text.
//
// EA/GA state lives directly on the feature row (announcement_state /
// ea_date / ga_date / announcement_narrative). announcement_state stores
// only the override — null means "follow the flag rollout" (Auto). The
// effective state combines override + rollout via
// effectiveAnnouncementState(); see shared/types.ts. Timeline only picks up
// rows with a concrete ea_date/ga_date, so a feature needs one to appear
// there (a fuzzy ga_date target is a projection, not a milestone).
import { useEffect, useState } from "react";
import { Link, useLocation, useRoute } from "wouter";
import {
  announcementStateLabel,
  deriveAnnouncementStateFromRollout,
  effectiveAnnouncementState,
  type EnrichedFeature,
  readinessLabel,
  type Surface,
} from "@release-hub/shared";
import {
  useFeature,
  useFeatureFamilies,
} from "@/api/queries";
import {
  useCreateFamily,
  useDeleteFeature,
  usePatchFeature,
  usePublishFeature,
} from "@/api/mutations";
import { ApiError } from "@/api/client";
import { useEffectiveRole } from "@/auth/role";
import { AutofillPanel } from "@/components/AutofillPanel";
import { AutoTextarea } from "@/components/AutoTextarea";
import { EditorHelp } from "@/components/EditorHelp";
import { EnabledWorkspacesPanel } from "@/components/EnabledWorkspacesPanel";
import { ExportTextModal } from "@/components/ExportTextModal";
import { FeatureAnnouncementCard } from "@/components/FeedCard";
import { FlagChip } from "@/components/FlagChip";
import { FlagPicker } from "@/components/FlagPicker";
import { HistoryDrawer } from "@/components/HistoryDrawer";
import { LivePreviewPanel } from "@/components/LivePreviewPanel";
import { MediaGallery } from "@/components/MediaGallery";
import { MediaUpload } from "@/components/MediaUpload";
import { InfoTip } from "@/components/InfoTip";
import { LoadingLine } from "@/components/LoadingLine";
import { SeedingSkeleton } from "@/components/SeedingSkeleton";
import { StateChip } from "@/components/StateChip";
import { WorkspaceHoverWrap } from "@/components/WorkspaceHoverWrap";
import { serializeFeature } from "@/lib/exportText";
import { DateToggle, GaTargetInput } from "@/components/DateInputs";
import { hintFor } from "@/lib/fieldHints";
import { Button } from "@/ui/button";
import { useConfirm } from "@/ui/confirm";
import { fmtDate, fmtDateTime } from "@/lib/format";
import {
  decodeProjectedGa,
  encodeProjectedGa,
  formatProjectedGa,
  type ProjectedGa,
} from "@/lib/projectedGa";
import { RolloutBadge } from "@/lib/rolloutBadge";
import { featureUrl } from "@/lib/url";
import { CopyLinkButton } from "@/components/CopyLinkButton";

export function FeatureDetail() {
  const [, params] = useRoute<{ id: string }>("/features/:id");
  const id = params?.id;
  const { data: feature, isPending, error } = useFeature(id);
  const { isEditor, canEditContent } = useEffectiveRole();
  const [, navigate] = useLocation();
  // Escape hatch if the background field-seed hangs (see SeedingSkeleton).
  const [bypassSeed, setBypassSeed] = useState(false);

  if (isPending) return <LoadingLine />;
  if (error) {
    const msg =
      error instanceof ApiError && error.status === 404
        ? `Feature "${id}" not found.`
        : `Failed to load: ${error instanceof Error ? error.message : "unknown"}`;
    return (
      <div className="space-y-4 py-6">
        <BackLink />
        <p className="text-destructive">{msg}</p>
      </div>
    );
  }
  if (!feature) return null;

  // Freshly created from notes — the LLM is filling fields in the background.
  // Show the seeding skeleton (and block editing) until it clears.
  if (feature.seeding_at && !bypassSeed) {
    return (
      <SeedingSkeleton
        message="Drafting your feature launch from the notes you pasted…"
        onBypass={() => setBypassSeed(true)}
      />
    );
  }

  // Editors get the full editor; Content Authors (PMM) get the same editor
  // capped to copy fields (contentOnly); everyone else is read-only.
  return isEditor || canEditContent ? (
    <EditorView
      feature={feature}
      contentOnly={!isEditor}
      onDeleted={() => navigate("/timeline")}
    />
  ) : (
    <ReadOnlyView feature={feature} />
  );
}

function BackLink() {
  return (
    <Link
      href="/timeline"
      className="inline-flex items-center gap-1 text-sm text-text-secondary no-underline hover:text-text"
    >
      ← Timeline
    </Link>
  );
}

// ─── Read-only view (viewers + sales) ────────────────────────────────

function ReadOnlyView({ feature }: { feature: EnrichedFeature }) {
  const { isSales } = useEffectiveRole();
  const [exportOpen, setExportOpen] = useState(false);

  return (
    <div className="space-y-6 py-6">
      <BackLink />
      <header className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="font-serif text-3xl">{feature.name}</h1>
          {feature.beta && (
            <span className="rounded-full bg-minor-soft px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-minor">
              Beta
            </span>
          )}
          <RolloutBadge rollout={feature.rollout} size="md" />
          <span className="ml-auto flex items-center gap-2">
            <CopyLinkButton
              url={featureUrl(feature.id)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-hover bg-card px-2.5 py-1 text-xs text-text-secondary hover:border-text/40 hover:text-text"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExportOpen(true)}
              title="Export this feature as plain text for pasting into an LLM"
            >
              Export
            </Button>
          </span>
        </div>
        {feature.pitch && (
          <p className="text-lg leading-relaxed text-text-secondary">
            {feature.pitch}
          </p>
        )}
      </header>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 rounded-lg border border-border bg-card p-5 md:grid-cols-[max-content_1fr]">
        <MetaRow label="Family" value={feature.feature_family} />
        <MetaRow
          label="Flags"
          value={
            feature.flag_ids.length === 0 ? null : (
              <div className="flex flex-wrap gap-1.5">
                {feature.flag_ids.map((fid) => (
                  <FlagChip key={fid} flagId={fid} />
                ))}
              </div>
            )
          }
        />
        <MetaRow label="Point of contact" value={feature.owner_email} />
        <MetaRow label="Web" value={feature.web_note} />
        <MetaRow label="Mobile" value={feature.mobile_note} />
        <MetaRow
          label="Helpful links / notes"
          value={
            feature.links_notes ? (
              <span className="whitespace-pre-wrap">{feature.links_notes}</span>
            ) : null
          }
        />
        <MetaRow label="Audience" value={feature.audience} />
      </dl>

      <ReadOnlyLaunchState feature={feature} isSales={isSales} />

      {feature.media_urls.length > 0 && (
        <section>
          <h3 className="mb-2 font-serif text-lg">Images</h3>
          <MediaGallery
            urls={feature.media_urls}
            captions={feature.media_captions}
          />
        </section>
      )}

      <EnabledWorkspacesPanel flags={feature.flags} />

      <ExportTextModal
        open={exportOpen}
        onOpenChange={setExportOpen}
        text={exportOpen ? serializeFeature(feature) : ""}
      />
    </div>
  );
}

/** Read-view "Current state" card. Bundles state + date + narrative +
 *  GA target + known gaps in one place. Renders only if any of those are
 *  set. Sales tier hides forward-looking GA targets. */
function ReadOnlyLaunchState({
  feature,
  isSales,
}: {
  feature: EnrichedFeature;
  isSales: boolean;
}) {
  // Effective state = editor override, or (when null) whatever the flag
  // rollout implies. Read view doesn't distinguish auto vs. override —
  // just shows the resolved state.
  const effState = effectiveAnnouncementState(
    feature.announcement_state,
    feature.rollout,
  );
  // Concrete milestone date to show beside the state chip: GA date if the
  // GA target is a real date, else the EA date. Fuzzy GA targets surface
  // in the "GA target" row below instead.
  const gaKind = decodeProjectedGa(feature.ga_date).kind;
  const milestoneDate =
    effState === "GA" && gaKind === "date"
      ? feature.ga_date
      : feature.ea_date && /^\d{4}-/.test(feature.ea_date)
        ? feature.ea_date
        : null;
  // Forward-looking GA target — shown when we haven't GA'd yet. Hidden from
  // sales (they shouldn't preview unshipped plans).
  const gaTargetLabel = !isSales ? formatProjectedGa(feature.ga_date) : "";
  const showGaTarget =
    !isSales && gaTargetLabel !== "" && effState !== "GA";
  const showGaps = feature.known_gaps.length > 0;
  const showPlatform = !!(feature.web_note || feature.mobile_note);
  const hasAnything =
    effState != null ||
    showGaTarget ||
    showGaps ||
    showPlatform ||
    feature.beta;
  if (!hasAnything) return null;
  return (
    <section>
      <h3 className="mb-2 font-serif text-lg">Current state</h3>
      <div className="space-y-3 rounded-lg border border-border bg-card p-4">
        {effState && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <WorkspaceHoverWrap flags={feature.flags} title={feature.name}>
              <StateChip
                kind={effState === "GA" ? "ga" : effState === "IN_DEV" ? "in_development" : "ea"}
                label={announcementStateLabel(effState)}
              />
            </WorkspaceHoverWrap>
            {milestoneDate && !isSales && (
              <time className="text-text-secondary">
                {fmtDate(milestoneDate)}
              </time>
            )}
          </div>
        )}
        {showGaTarget && (
          <div className="flex flex-wrap items-baseline gap-2 text-sm">
            <span className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
              GA target
            </span>
            <span className="text-text">{gaTargetLabel}</span>
          </div>
        )}
        {effState && feature.announcement_narrative && (
          <div>
            <div className="mb-1 text-xs font-medium uppercase tracking-wider text-text-tertiary">
              Narrative
            </div>
            <p className="text-text-secondary">
              {feature.announcement_narrative}
            </p>
          </div>
        )}
        <div className="flex items-baseline gap-2 text-sm">
          <span className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
            Readiness
          </span>
          <span
            className={
              feature.beta
                ? "rounded-full bg-minor-soft px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-minor"
                : "rounded-full bg-border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-text-secondary"
            }
          >
            {readinessLabel(feature.beta)}
          </span>
        </div>
        {feature.web_note && (
          <div className="flex items-baseline gap-2 text-sm">
            <span className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
              Web
            </span>
            <span className="text-text">{feature.web_note}</span>
          </div>
        )}
        {feature.mobile_note && (
          <div className="flex items-baseline gap-2 text-sm">
            <span className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
              Mobile
            </span>
            <span className="text-text">{feature.mobile_note}</span>
          </div>
        )}
        {showGaps && (
          <div>
            <div className="mb-1 text-xs font-medium uppercase tracking-wider text-text-tertiary">
              Known gaps
            </div>
            <ul className="list-disc space-y-1 pl-6 text-sm text-text">
              {feature.known_gaps.map((g, i) => (
                <li key={i}>{g}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Editor view (inline inputs, no mode toggle) ─────────────────────

function EditorView({
  feature,
  onDeleted,
  contentOnly = false,
}: {
  feature: EnrichedFeature;
  onDeleted: () => void;
  /** Content Author (PMM) mode: only customer-facing copy is editable.
   *  State, dates, flags, readiness, publish, delete, and history are all
   *  hidden — matching the server-side PMM whitelist in db.ts. */
  contentOnly?: boolean;
}) {
  const patch = usePatchFeature();
  const publish = usePublishFeature();
  const del = useDeleteFeature();
  const confirm = useConfirm();
  const [autofillOpen, setAutofillOpen] = useState(false);

  // Images are patched immediately on add/remove/caption-blur (like the
  // release editor's media), independent of the Save button that batches the
  // text fields below.
  const doMedia = (urls: string[], captions: Record<string, string>) =>
    patch.mutate({ id: feature.id, patch: { media_urls: urls, media_captions: captions } });

  const [name, setName] = useState(feature.name);
  const [pitch, setPitch] = useState(feature.pitch ?? "");
  const [family, setFamily] = useState(feature.feature_family ?? "");
  const [beta, setBeta] = useState(feature.beta);
  const [flags, setFlags] = useState<string[]>(feature.flag_ids);
  const [owner, setOwner] = useState(feature.owner_email ?? "");
  // Platform selector + per-platform notes (matches the create wizard).
  const [surfaceWeb, setSurfaceWeb] = useState(feature.surface.includes("web"));
  const [surfaceMobile, setSurfaceMobile] = useState(
    feature.surface.some((s) => s === "ios" || s === "android"),
  );
  const [webNote, setWebNote] = useState(feature.web_note ?? "");
  const [mobileNote, setMobileNote] = useState(feature.mobile_note ?? "");
  const [linksNotes, setLinksNotes] = useState(feature.links_notes ?? "");
  const [gaps, setGaps] = useState(feature.known_gaps.join("\n"));
  // Who this launch is for — a specific customer/account or a persona. Lives
  // in the metadata block; PMM/Sales + the LLM digests consume it directly.
  const [audience, setAudience] = useState(feature.audience ?? "");

  // Current state fields. Semantics:
  //   annState = "" | EA | GA — the access override ("" = Auto/follow flag)
  //   eaDate   = "" | ISO     — concrete EA transition date
  //   gaTarget = ProjectedGa  — the single GA target: a concrete date (a real
  //                             milestone) OR a coarse forward projection.
  const [annState, setAnnState] = useState<"" | "EA" | "GA">(
    feature.announcement_state ?? "",
  );
  const [eaDate, setEaDate] = useState<string>(feature.ea_date ?? "");
  const [gaTarget, setGaTarget] = useState<ProjectedGa>(
    decodeProjectedGa(feature.ga_date),
  );
  const [annNarrative, setAnnNarrative] = useState(
    feature.announcement_narrative ?? "",
  );

  const [saveErr, setSaveErr] = useState<string>("");
  const [pubErr, setPubErr] = useState<string>("");
  const [deleteErr, setDeleteErr] = useState<string>("");
  const [exportOpen, setExportOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Sync local state when the server sends fresh data (e.g. after Save or
  // after publish/unpublish updates the row). Skip during pending patches
  // so we don't clobber active typing.
  useEffect(() => {
    if (patch.isPending) return;
    setName(feature.name);
    setPitch(feature.pitch ?? "");
    setFamily(feature.feature_family ?? "");
    setBeta(feature.beta);
    setFlags(feature.flag_ids);
    setOwner(feature.owner_email ?? "");
    setSurfaceWeb(feature.surface.includes("web"));
    setSurfaceMobile(feature.surface.some((s) => s === "ios" || s === "android"));
    setWebNote(feature.web_note ?? "");
    setMobileNote(feature.mobile_note ?? "");
    setLinksNotes(feature.links_notes ?? "");
    setGaps(feature.known_gaps.join("\n"));
    setAudience(feature.audience ?? "");
    setAnnState(feature.announcement_state ?? "");
    setEaDate(feature.ea_date ?? "");
    setGaTarget(decodeProjectedGa(feature.ga_date));
    setAnnNarrative(feature.announcement_narrative ?? "");
  }, [feature, patch.isPending]);

  const lines = (s: string) =>
    s.split("\n").map((x) => x.trim()).filter(Boolean);

  // annState "" = Auto (follow flag rollout); explicit EA/GA overrides.
  // Effective state drives which sub-fields are meaningful — a feature
  // in Auto that resolves to EA/GA still gets a date + narrative slot.
  const autoDerivedState = deriveAnnouncementStateFromRollout(feature.rollout);
  const currentAnnState = annState === "" ? null : annState;
  const effectiveState = currentAnnState ?? autoDerivedState;
  const currentEaDate = eaDate.trim() || null;
  const currentGaDate = encodeProjectedGa(gaTarget);
  const currentAnnNarrative = effectiveState
    ? annNarrative.trim() || null
    : null;

  const surfaceArr: Surface[] = [
    ...(surfaceWeb ? (["web"] as Surface[]) : []),
    ...(surfaceMobile ? (["ios", "android"] as Surface[]) : []),
  ];
  const currentWebNote = surfaceWeb ? webNote.trim() || null : null;
  const currentMobileNote = surfaceMobile ? mobileNote.trim() || null : null;
  const currentLinks = linksNotes.trim() || null;

  const dirty =
    name !== feature.name ||
    pitch !== (feature.pitch ?? "") ||
    family !== (feature.feature_family ?? "") ||
    beta !== feature.beta ||
    flags.join("\n") !== feature.flag_ids.join("\n") ||
    owner !== (feature.owner_email ?? "") ||
    surfaceArr.join(",") !== feature.surface.join(",") ||
    currentWebNote !== (feature.web_note ?? null) ||
    currentMobileNote !== (feature.mobile_note ?? null) ||
    currentLinks !== (feature.links_notes ?? null) ||
    gaps !== feature.known_gaps.join("\n") ||
    audience !== (feature.audience ?? "") ||
    currentAnnState !== feature.announcement_state ||
    currentEaDate !== (feature.ea_date ?? null) ||
    currentGaDate !== (feature.ga_date ?? null) ||
    currentAnnNarrative !== feature.announcement_narrative;

  const save = async () => {
    setSaveErr("");
    // Copy fields a Content Author may change. Everything else (flags, beta,
    // state, dates) is editor-only and is stripped for contentOnly saves — the
    // server enforces the same whitelist, this just keeps the request clean.
    const contentPatch = {
      name: name.trim(),
      pitch,
      feature_family: family.trim() || null,
      owner_email: owner.trim() || null,
      surface: surfaceArr,
      web_note: currentWebNote,
      mobile_note: currentMobileNote,
      links_notes: currentLinks,
      known_gaps: lines(gaps),
      audience: audience.trim() || null,
      announcement_narrative: currentAnnNarrative,
    };
    const fullPatch = {
      ...contentPatch,
      beta,
      flag_ids: flags,
      announcement_state: currentAnnState,
      ea_date: currentEaDate,
      ga_date: currentGaDate,
    };
    try {
      await patch.mutateAsync({
        id: feature.id,
        patch: contentOnly ? contentPatch : fullPatch,
      });
    } catch (e) {
      setSaveErr(errMsg(e));
    }
  };

  const doPublish = async () => {
    setPubErr("");
    try {
      await publish.mutateAsync({
        id: feature.id,
        publish: feature.status !== "published",
      });
    } catch (e) {
      setPubErr(errMsg(e));
    }
  };

  const doDelete = async () => {
    setDeleteErr("");
    const ok = await confirm({
      title: "Delete this feature launch?",
      description: (
        <>
          Removes <strong>{feature.name}</strong> entirely. This can't be
          undone. The underlying squapi flag is untouched.
        </>
      ),
      confirmLabel: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await del.mutateAsync(feature.id);
      onDeleted();
    } catch (e) {
      setDeleteErr(errMsg(e));
    }
  };

  // Compose an EnrichedFeature that reflects the live draft state so the
  // preview shows what publishing right now would produce. Server-owned
  // fields (rollout, flags detail, missing_flags, ids) come from `feature`
  // untouched; editable fields override.
  const previewFeature: EnrichedFeature = {
    ...feature,
    name: name.trim() || feature.name,
    pitch,
    feature_family: family.trim() || null,
    beta,
    flag_ids: flags,
    owner_email: owner.trim() || null,
    surface: surfaceArr,
    web_note: currentWebNote,
    mobile_note: currentMobileNote,
    links_notes: currentLinks,
    known_gaps: lines(gaps),
    audience: audience.trim() || null,
    announcement_state: currentAnnState,
    ea_date: currentEaDate,
    ga_date: currentGaDate,
    announcement_narrative: currentAnnNarrative,
  };

  return (
    <div className="space-y-6 py-6">
      <BackLink />
      <AutofillPanel
        kind="feature"
        entityId={feature.id}
        current={feature}
        open={autofillOpen}
        onOpenChange={setAutofillOpen}
      />
      {/* Header with editable name + pitch */}
      <header className="space-y-3">
        <div className="flex flex-wrap items-start gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Feature launch name"
            className="min-w-0 flex-1 border-b border-transparent bg-transparent font-serif text-3xl leading-tight focus:border-accent focus:outline-none"
          />
          <span className="mt-3">
            <InfoTip>{hintFor("name")}</InfoTip>
          </span>
          {!contentOnly && (
            <button
              type="button"
              onClick={() => setAutofillOpen(true)}
              className="mt-2 rounded-md border border-border-hover bg-card px-3 py-1 text-sm text-text-secondary hover:border-text/40 hover:text-text"
              title="Paste a blurb and let AI propose field values"
            >
              Autofill from notes
            </button>
          )}
          {feature.status === "draft" && (
            <span className="mt-2 rounded-full bg-border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
              Draft
            </span>
          )}
          {beta && (
            <span className="mt-2 rounded-full bg-minor-soft px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-minor">
              Beta
            </span>
          )}
          <div className="mt-2">
            <RolloutBadge rollout={feature.rollout} size="md" />
          </div>
        </div>
        <div>
          <span className="flex items-center pl-2 text-[11px] font-medium uppercase tracking-wider text-text-tertiary">
            Pitch
            <InfoTip>{hintFor("pitch")}</InfoTip>
          </span>
          <textarea
            value={pitch}
            onChange={(e) => setPitch(e.target.value)}
            placeholder="One-line customer summary — what does this ship?"
            rows={2}
            className="w-full resize-none rounded-md border border-transparent bg-transparent p-2 text-lg leading-relaxed text-text-secondary hover:border-border-hover focus:border-accent focus:bg-card focus:text-text focus:outline-none"
          />
        </div>
      </header>

      {/* Metadata — same layout as read view, but each value is an input */}
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 rounded-lg border border-border bg-card p-5 md:grid-cols-[max-content_1fr]">
        <MetaRow
          label="Family"
          hint={hintFor("feature_family")}
        >
          <FamilyDropdown value={family} onChange={setFamily} />
        </MetaRow>

        <MetaRow
          label="Flags"
          hint={hintFor("flag_ids")}
        >
          {contentOnly ? (
            // Flags drive rollout/state derivation, so they're editor-only.
            // Content Authors see them as static context.
            flags.length === 0 ? (
              <span className="text-text-tertiary">—</span>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {flags.map((fid) => (
                  <FlagChip key={fid} flagId={fid} />
                ))}
              </div>
            )
          ) : (
            <FlagPicker value={flags} onChange={setFlags} />
          )}
        </MetaRow>

        {feature.missing_flags.length > 0 && (
          <MetaRow
            label="Missing"
            hint="Flag IDs referenced here that don't exist in squapi. Either remove them or ask squapi why they've gone."
          >
            <span className="text-destructive">
              {feature.missing_flags.join(", ")}
            </span>
          </MetaRow>
        )}

        <MetaRow
          label="Point of contact"
          hint={hintFor("owner_email")}
        >
          <input
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            placeholder="you@squint.ai"
            className={inputCls}
          />
        </MetaRow>

        <MetaRow
          label="Platforms"
          hint={hintFor("platforms")}
        >
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 accent-accent"
                checked={surfaceWeb}
                onChange={(e) => setSurfaceWeb(e.target.checked)}
              />
              Web
            </label>
            {surfaceWeb && (
              <input
                value={webNote}
                onChange={(e) => setWebNote(e.target.value)}
                placeholder='Note about web (optional) — e.g. "dashboard only"'
                className={inputCls}
              />
            )}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 accent-accent"
                checked={surfaceMobile}
                onChange={(e) => setSurfaceMobile(e.target.checked)}
              />
              Mobile
            </label>
            {surfaceMobile && (
              <input
                value={mobileNote}
                onChange={(e) => setMobileNote(e.target.value)}
                placeholder='Note about mobile (optional) — e.g. "iOS 16+ only"'
                className={inputCls}
              />
            )}
          </div>
        </MetaRow>

        <MetaRow
          label="Helpful links / notes"
          hint={hintFor("links_notes")}
        >
          <textarea
            value={linksNotes}
            onChange={(e) => setLinksNotes(e.target.value)}
            rows={3}
            placeholder="Paste links or notes — PRD, demo, tickets…"
            className={inputCls}
          />
        </MetaRow>

        <MetaRow label="Audience" hint={hintFor("audience")}>
          <input
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            placeholder="Who is this for? e.g. Chick-fil-A ops leads, or enterprise admins"
            className={inputCls}
          />
        </MetaRow>

        <MetaRow label="Images">
          <MediaUpload
            urls={feature.media_urls}
            captions={feature.media_captions}
            onChange={doMedia}
          />
        </MetaRow>
      </dl>

      {/* Current state — the rollout lifecycle for this feature. State
          defaults to Auto, which tracks the flag rollout; editors can
          override to explicit EA/GA. Sub-fields switch on the EFFECTIVE
          state (Auto's derived value or the override):
            GA   → GA target (concrete) + narrative
            EA   → EA date + GA target + narrative
            none → GA target
          Beta / known gaps show regardless. */}
      <section className="space-y-4 rounded-lg border border-border bg-card p-5">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-serif text-lg">Current state</h3>
          <p className="text-xs text-text-tertiary">
            {contentOnly
              ? "Access, dates, and readiness are managed by EPD."
              : "State + date puts this feature on the Timeline."}
          </p>
        </div>

        {contentOnly && (
          // Read-only context for Content Authors: the resolved access state,
          // its milestone date, and readiness — none of which they can change,
          // but which the narrative below should stay consistent with.
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {effectiveState && (
              <StateChip
                kind={
                  effectiveState === "GA"
                    ? "ga"
                    : effectiveState === "IN_DEV"
                      ? "in_development"
                      : "ea"
                }
                label={announcementStateLabel(effectiveState)}
              />
            )}
            {currentEaDate && (
              <span className="text-text-secondary">EA {fmtDate(currentEaDate)}</span>
            )}
            {formatProjectedGa(currentGaDate) && (
              <span className="text-text-secondary">
                GA {formatProjectedGa(currentGaDate)}
              </span>
            )}
            <span
              className={
                beta
                  ? "rounded-full bg-minor-soft px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-minor"
                  : "rounded-full bg-border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-text-secondary"
              }
            >
              {readinessLabel(beta)}
            </span>
          </div>
        )}

        {!contentOnly && (
        <>
        <div>
          <FieldLabel hint={hintFor("announcement_state")}>
            State
          </FieldLabel>
          <div className="inline-flex rounded-md border border-border-hover bg-bg p-0.5">
            {(
              [
                ["", "Auto"],
                ["EA", "EA"],
                ["GA", "GA"],
              ] as ["" | "EA" | "GA", string][]
            ).map(([s, label]) => (
              <button
                key={s || "auto"}
                type="button"
                onClick={() => setAnnState(s)}
                className={
                  annState === s
                    ? "rounded-sm bg-card px-3 py-1 text-xs font-medium text-text shadow-sm"
                    : "rounded-sm px-3 py-1 text-xs text-text-secondary hover:text-text"
                }
              >
                {label}
              </button>
            ))}
          </div>
          {/* Explain what Auto resolves to vs. an explicit override. Editors
              are almost always fine with Auto — this hint tells them
              exactly what that means for this feature. */}
          <p className="mt-1 text-xs text-text-tertiary">
            {annState === ""
              ? autoDerivedState
                ? `Auto → ${autoDerivedState} (flag rollout is ${feature.rollout.label}).`
                : `Auto → no state (flag rollout is ${feature.rollout.label}).`
              : autoDerivedState === annState
                ? `Overriding, but matches flag rollout (${feature.rollout.label}).`
                : `Overriding flag rollout (${feature.rollout.label} → auto would be ${autoDerivedState ?? "none"}).`}
          </p>
        </div>

        <div>
          <FieldLabel hint={hintFor("ea_date")}>EA date</FieldLabel>
          <DateToggle label="EA date" value={eaDate} onChange={setEaDate} />
          <p className="mt-1 text-xs text-text-tertiary">
            Untick to leave unset. A past date reads as actual, a future date as a target.
          </p>
        </div>

        <div>
          <FieldLabel hint={hintFor("ga_date")}>GA target</FieldLabel>
          <GaTargetInput value={gaTarget} onChange={setGaTarget} />
          <p className="mt-1 text-xs text-text-tertiary">
            A concrete date is a real GA milestone (past = shipped, future =
            scheduled) and appears on the Timeline. A quarter/week/TBD is a
            forward projection and doesn't.
          </p>
        </div>
        </>
        )}

        {effectiveState && (
          <div>
            <FieldLabel hint={hintFor("announcement_narrative")}>
              Narrative
            </FieldLabel>
            <AutoTextarea
              value={annNarrative}
              onChange={(e) => setAnnNarrative(e.target.value)}
              placeholder="Customer-facing note for the Timeline. e.g. 'Enterprise Console is now GA — accessible under Admin → Console.'"
              className={`${inputCls} min-h-[7rem]`}
            />
          </div>
        )}

        {!contentOnly && (
        <div>
          <FieldLabel hint={hintFor("beta")}>
            Readiness
          </FieldLabel>
          <div className="inline-flex rounded-md border border-border-hover bg-bg p-0.5">
            {(
              [
                [false, "Stable"],
                [true, "Beta"],
              ] as [boolean, string][]
            ).map(([v, label]) => (
              <button
                key={String(v)}
                type="button"
                onClick={() => setBeta(v)}
                className={
                  beta === v
                    ? "rounded-sm bg-card px-3 py-1 text-xs font-medium text-text shadow-sm"
                    : "rounded-sm px-3 py-1 text-xs text-text-secondary hover:text-text"
                }
              >
                {label}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-text-tertiary">
            Beta is a flavor tag — says nothing about access.
          </p>
        </div>
        )}

        <div>
          <FieldLabel hint={hintFor("known_gaps")}>
            Known gaps
          </FieldLabel>
          <textarea
            value={gaps}
            onChange={(e) => setGaps(e.target.value)}
            rows={3}
            placeholder="One per line — anything shipping-blocking or worth flagging."
            className={inputCls}
          />
        </div>
      </section>

      {/* Actions */}
      <section className="flex flex-wrap items-center gap-3">
        <Button onClick={save} disabled={!dirty || patch.isPending || !name.trim()}>
          {patch.isPending ? "Saving…" : "Save"}
        </Button>
        {!contentOnly && (
          <Button
            variant={feature.status === "published" ? "destructive" : "default"}
            onClick={doPublish}
            disabled={publish.isPending}
          >
            {publish.isPending
              ? "…"
              : feature.status === "published"
                ? "Unpublish"
                : "Publish"}
          </Button>
        )}
        <CopyLinkButton
          url={featureUrl(feature.id)}
          className="inline-flex items-center gap-1.5 rounded-md border border-border-hover bg-card px-2.5 py-1 text-xs text-text-secondary hover:border-text/40 hover:text-text"
        />
        <Button
          variant="ghost"
          onClick={() => setExportOpen(true)}
          title="Export this feature as plain text for pasting into an LLM"
        >
          Export
        </Button>
        {!contentOnly && (
          <Button
            variant="ghost"
            onClick={() => setHistoryOpen(true)}
            title="View field-level edit history for this feature"
          >
            History
          </Button>
        )}
        {!contentOnly && (
          <Button variant="ghost" onClick={doDelete} disabled={del.isPending}>
            Delete feature launch
          </Button>
        )}
        {saveErr && <span className="text-sm text-destructive">{saveErr}</span>}
        {pubErr && <span className="text-sm text-destructive">{pubErr}</span>}
        {deleteErr && (
          <span className="text-sm text-destructive">{deleteErr}</span>
        )}
      </section>

      <EditTrail
        by={feature.last_edited_by}
        at={feature.last_edited_at}
        onOpenHistory={contentOnly ? undefined : () => setHistoryOpen(true)}
      />

      <ExportTextModal
        open={exportOpen}
        onOpenChange={setExportOpen}
        text={exportOpen ? serializeFeature(feature) : ""}
      />

      <HistoryDrawer
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        entityType="feature_entries"
        entityId={feature.id}
        title={feature.name}
      />
    </div>
  );
}

/** Small "last edited by X on Y — view history →" line. Renders nothing
 *  if there's no recorded edit yet (a freshly-seeded feature). */
function EditTrail({
  by,
  at,
  onOpenHistory,
}: {
  by: string | null;
  at: string | null;
  /** Omit to hide the "View history" link (edit history is editor-only). */
  onOpenHistory?: () => void;
}) {
  if (!at) return null;
  const who = by?.split("@")[0] ?? "someone";
  return (
    <p className="text-xs text-text-tertiary">
      Last edited by <span className="font-medium text-text-secondary">{who}</span>{" "}
      · <time>{fmtDateTime(at)}</time>
      {onOpenHistory && (
        <>
          {" "}
          ·{" "}
          <button
            type="button"
            onClick={onOpenHistory}
            className="underline decoration-dotted hover:text-text"
          >
            View history
          </button>
        </>
      )}
    </p>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────

const inputCls =
  "w-full rounded-md border border-border-hover bg-bg px-3 py-1.5 text-sm focus:border-accent focus:outline-none";

/** Small uppercase label used by fields in the Current state section.
 *  Attaches an InfoTip when `hint` is provided so editors get a "what
 *  is this / how do I fill it" affordance on hover. */
function FieldLabel({
  hint,
  children,
}: {
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <label className="mb-1 flex items-center text-xs font-medium uppercase tracking-wider text-text-tertiary">
      {children}
      {hint && <InfoTip>{hint}</InfoTip>}
    </label>
  );
}

/** Common row shape for both read + edit views. The value column takes
 *  either static text (read) or an inline input (edit). `hint` shows
 *  an InfoTip icon next to the label — used only by the editor view.  */
function MetaRow({
  label,
  hint,
  value,
  children,
}: {
  label: string;
  hint?: React.ReactNode;
  value?: React.ReactNode;
  children?: React.ReactNode;
}) {
  const content = children ?? value;
  return (
    <>
      <dt className="pt-1.5 text-xs font-medium uppercase tracking-wider text-text-tertiary">
        <span className="inline-flex items-center">
          {label}
          {hint && <InfoTip>{hint}</InfoTip>}
        </span>
      </dt>
      <dd className="text-sm text-text">
        {content == null || content === "" ? (
          <span className="text-text-tertiary">—</span>
        ) : (
          content
        )}
      </dd>
    </>
  );
}

/** Family picker: dropdown of live feature_families. Orphans (name no
 *  longer in the list) still selectable so the editor can see + fix. */
const NEW_FAMILY = "__new__";

/** Family picker: dropdown of live feature_families, plus a "＋ New family…"
 *  option that swaps to an inline text field. Adding registers the family
 *  (POST /api/feature-families) so it's a first-class family, not an orphan
 *  string, then selects it. Orphans (a name no longer in the list) stay
 *  selectable so the editor can see + fix. */
function FamilyDropdown({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const { data: families, isPending } = useFeatureFamilies();
  const createFamily = useCreateFamily();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState("");
  const names = families?.map((f) => f.name) ?? [];
  const isOrphan = value !== "" && !names.includes(value);

  const submit = async () => {
    const name = draft.trim();
    if (!name) return;
    setErr("");
    try {
      // Reuse an existing family on a case-insensitive name match rather than
      // erroring on a duplicate.
      const existing = names.find((n) => n.toLowerCase() === name.toLowerCase());
      if (!existing) await createFamily.mutateAsync(name);
      onChange(existing ?? name);
      setAdding(false);
      setDraft("");
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  if (adding) {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit();
              } else if (e.key === "Escape") {
                setAdding(false);
                setDraft("");
                setErr("");
              }
            }}
            placeholder="New family name"
            className={inputCls}
          />
          <Button
            size="sm"
            onClick={submit}
            disabled={!draft.trim() || createFamily.isPending}
          >
            {createFamily.isPending ? "Adding…" : "Add"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setAdding(false);
              setDraft("");
              setErr("");
            }}
          >
            Cancel
          </Button>
        </div>
        {err && <p className="text-xs text-destructive">{err}</p>}
      </div>
    );
  }

  return (
    <select
      value={value}
      onChange={(e) => {
        if (e.target.value === NEW_FAMILY) {
          setAdding(true);
          return;
        }
        onChange(e.target.value);
      }}
      disabled={isPending}
      className={inputCls}
    >
      <option value="">— No family —</option>
      {isOrphan && <option value={value}>(orphaned) {value}</option>}
      {names.map((n) => (
        <option key={n} value={n}>
          {n}
        </option>
      ))}
      <option value={NEW_FAMILY}>＋ New family…</option>
    </select>
  );
}

function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.detail;
  if (e instanceof Error) return e.message;
  return String(e);
}
